import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  IOWidget,
  Fun,
  fromJSX,
  onlyIf,
  Widget,
  any,
  async,
} from 'widgets-for-react'
import { generateBreadcrumbs } from '../../../utils/breadcrumbs'
import { capitalize } from '../../../utils/utils'
import { CollectionsLoaderState } from '../collections/collectionsLoader/collectionsLoaderState'
import { PageHeaderLayout } from '../pageHeader/pageHeaderLayout'
import { paywallWidget } from '../paywall/paywallWidget'
import { RootLayout } from '../rootLayout'
import { SponsorData } from '../sponsor/sponsorState'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import { BlocksState } from './blocks/blocksState'
import { blocksWidget } from './blocks/blocksWidget'
import {
  SharepointPageState,
  SharepointPageData,
  sharepointPageStateUpdaters,
} from './sharepointPageState'

export type SharepointPageType = 'knowledge' | 'blog' | 'page' | 'dish'

export const sharepointPageWidget =
  (
    pageType: SharepointPageType,
    collectionsState: CollectionsLoaderState,
    isLoggedIn: boolean,
    isPrintMode: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>
  ): IOWidget<
    SharepointPageState,
    Fun<
      SharepointPageState,
      SharepointPageState & { reloadCollections?: boolean }
    >
  > =>
  (pageState) => {
    return any([
      async(pageState.sharepointPageDataState).map(
        sharepointPageStateUpdaters.sharepointPageDataStateUpdater
      ),
      async(pageState.sponsorDataState).map(
        sharepointPageStateUpdaters.updateSponsorDataState
      ),
      pageState.sharepointPageDataState.kind == 'loaded'
        ? (pageState.sharepointPageDataState.value.ContentType ===
            'Knowledge page' &&
            pageType === 'knowledge') ||
          (pageState.sharepointPageDataState.value.ContentType ===
            'Dish page' &&
            pageType === 'dish') ||
          (pageState.sharepointPageDataState.value.ContentType ===
            'Blog page' &&
            pageType === 'blog') ||
          (pageState.sharepointPageDataState.value.ContentType ===
            'Gastronomixs page' &&
            pageType === 'page')
          ? pageState.sharepointPageDataState.value.Public
            ? paywallWidget(
                isLoggedIn,
                userSubscriptionsState,
                undefined,
                false,
                'preFooterCta'
              )(
                pageBaseWidget(
                  pageType,
                  collectionsState,
                  isLoggedIn,
                  isPrintMode,
                  pageState.sharepointPageDataState.value,
                  pageState.sponsorDataState,
                  pageState.blocksState,
                  false,
                  true
                ),
                pageBaseWidget(
                  pageType,
                  collectionsState,
                  isLoggedIn,
                  isPrintMode,
                  pageState.sharepointPageDataState.value,
                  pageState.sponsorDataState,
                  pageState.blocksState,
                  false,
                  false
                )
              )
            : paywallWidget(
                isLoggedIn,
                userSubscriptionsState,
                pageState.sharepointPageDataState.value.SubscriptionTypesId
              )(
                pageBaseWidget(
                  pageType,
                  collectionsState,
                  isLoggedIn,
                  isPrintMode,
                  pageState.sharepointPageDataState.value,
                  pageState.sponsorDataState,
                  pageState.blocksState,
                  true,
                  false
                ),
                pageBaseWidget(
                  pageType,
                  collectionsState,
                  isLoggedIn,
                  isPrintMode,
                  pageState.sharepointPageDataState.value,
                  pageState.sponsorDataState,
                  pageState.blocksState,
                  false,
                  false
                )
              )
          : fromJSX(() => <RootLayout.Error />)
        : pageState.sharepointPageDataState.kind === 'unloaded'
        ? fromJSX(() => <RootLayout.Error />)
        : pageState.sharepointPageDataState.kind === 'loading' ||
          pageState.sharepointPageDataState.retryCount == undefined ||
          pageState.sharepointPageDataState.retryCount < 10
        ? fromJSX(() => <RootLayout.Loader hasWrapper={true} showText={true} />)
        : fromJSX(() => <RootLayout.Error />),
    ])
  }

const pageBaseWidget = (
  pageType: SharepointPageType,
  collectionsState: CollectionsLoaderState,
  isLoggedIn: boolean,
  isPrintMode: boolean,
  sharepointPageData: SharepointPageData,
  sponsorDataState: AsyncState<SponsorData>,
  blocksState: BlocksState,
  hasPaywall: boolean,
  hasPrefooter: boolean
): Widget<
  Fun<
    SharepointPageState,
    SharepointPageState & { reloadCollections?: boolean }
  >
> =>
  any([
    fromJSX(() => (
      <PageHeaderLayout
        kind={
          // Force knowledge pageheader for all Sharepoint pages, except a blog with image
          pageType == 'blog' && sharepointPageData.HeaderImage.Url
            ? 'blog'
            : pageType == 'dish'
            ? 'dish'
            : 'knowledge'
        }
        title={sharepointPageData.HeaderTitle}
        intro={sharepointPageData.HeaderDescription}
        image={sharepointPageData.HeaderImage}
        date={
          pageType === 'blog' &&
          !isNaN(Date.parse(sharepointPageData.PublicationDateBegin))
            ? new Date(sharepointPageData.PublicationDateBegin)
            : undefined
        }
        sponsor={
          sponsorDataState.kind == 'loaded'
            ? {
                sponsorData: sponsorDataState.value,
                copy: i18next.t(
                  `${
                    pageType == 'dish' ? 'dishes' : pageType
                  }:sponsored${capitalize(
                    pageType == 'page' ? 'Knowledge' : pageType
                  )}`
                ),
                language: i18next.language,
              }
            : undefined
        }
        breadcrumbs={
          hasPaywall || hasPrefooter ? undefined : generateBreadcrumbs()
        }
      />
    )),
    onlyIf(
      !hasPaywall,
      blocksWidget(
        collectionsState,
        isLoggedIn,
        isPrintMode,
        pageType
      )(blocksState)
    ).map(sharepointPageStateUpdaters.updateBlocksState),
  ])
